import React, { useState, useEffect, useCallback } from "react";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Switch from "@material-ui/core/Switch";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useMenuStyles } from "../../components/menuStyles";
import Shell from "./Shell";
import { useAdminStyles, brl, num } from "./styles";

// Planos e franquias. O aviso de quantos clientes o plano tem fica à vista o
// tempo todo: mudar uma franquia muda a régua de todos eles na hora, sem
// versionamento — não é uma edição de cadastro qualquer.
const Plans = () => {
  const classes = useAdminStyles();
  const menu = useMenuStyles();
  const [plans, setPlans] = useState([]);
  const [fields, setFields] = useState([]);
  const [edit, setEdit] = useState(null); // plano em edição
  const [creating, setCreating] = useState(false); // criando plano novo
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/plans");
      setPlans(data.plans || []);
      setFields(data.limitFields || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const abrir = p => {
    setEdit(p);
    setReason("");
    setForm({
      name: p.name,
      preco: (p.monthlyPriceCents / 100).toFixed(2).replace(".", ","),
      isPublic: p.isPublic,
      overageMultiplier: String(p.overageMultiplier ?? 1),
      // vazio = ilimitado, e é assim que volta para o servidor
      limits: Object.fromEntries((fields || []).map(f => [f.key, p.limits[f.key] == null ? "" : String(p.limits[f.key])])),
    });
  };

  const abrirNovo = () => {
    setEdit(null);
    setCreating(true);
    setReason("");
    setForm({
      code: "",
      name: "",
      preco: "",
      isPublic: false, // plano customizado por conta nasce INTERNO
      overageMultiplier: "0",
      limits: Object.fromEntries((fields || []).map(f => [f.key, ""])),
    });
  };

  const criar = async () => {
    setBusy(true);
    try {
      await api.post("/admin/plans", {
        code: form.code,
        name: form.name,
        monthlyPriceCents: form.preco
          ? Math.round(Number(String(form.preco).replace(/\./g, "").replace(",", ".")) * 100)
          : null,
        isPublic: form.isPublic,
        overageMultiplier: Number(form.overageMultiplier || 0),
        limits: Object.fromEntries(Object.entries(form.limits).map(([k, v]) => [k, v === "" ? null : v])),
        reason,
      });
      toast.success("Plano criado.");
      setCreating(false);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const salvar = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/plans/${edit.id}`, {
        name: form.name,
        monthlyPriceCents: Math.round(Number(String(form.preco).replace(/\./g, "").replace(",", ".")) * 100),
        isPublic: form.isPublic,
        overageMultiplier: Number(form.overageMultiplier),
        limits: Object.fromEntries(Object.entries(form.limits).map(([k, v]) => [k, v === "" ? null : v])),
        reason,
      });
      toast.success(`Plano atualizado — ${num(data.afetados)} cliente(s) afetado(s).`);
      setEdit(null);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Planos e franquias">
      <div className={classes.card}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className={classes.cardTitle} style={{ flex: 1 }}>Planos</div>
          <Button size="small" variant="contained" color="primary" onClick={abrirNovo}>
            Novo plano
          </Button>
        </div>
        <div className={classes.cardSub}>
          Alterar um plano vale imediatamente para todos os clientes nele — não há versionamento. Toda alteração exige motivo e vai para a auditoria. Para limites específicos de uma conta, crie um plano dedicado (interno) e atribua a conta a ele em Usuários.
        </div>
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                {["Plano", "Mensalidade", "Clientes", "Assinaturas ativas", "Excedente", "Visível", ""].map((h, i) => (
                  <th key={h} className={`${classes.th} ${classes.thPlain}`} style={i >= 2 && i <= 4 ? { textAlign: "right" } : undefined}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {plans.map(p => (
                <tr key={p.id}>
                  <td className={classes.td}>
                    <div className={classes.strong}>{p.name}</div>
                    <div className={classes.muted}>{p.code}</div>
                  </td>
                  <td className={classes.td}>{brl(p.monthlyPriceCents)}</td>
                  <td className={`${classes.td} ${classes.tdNum}`}>{num(p.usuarios)}</td>
                  <td className={`${classes.td} ${classes.tdNum}`}>{num(p.assinaturasAtivas)}</td>
                  <td className={`${classes.td} ${classes.tdNum}`}>{p.overageMultiplier}×</td>
                  <td className={classes.td}>
                    <span className={`${classes.pill} ${p.isPublic ? classes.ok : classes.neutral}`}>
                      {p.isPublic ? "Sim" : "Interno"}
                    </span>
                  </td>
                  <td className={classes.td}><Button size="small" onClick={() => abrir(p)}>Editar</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Franquias por plano</div>
        <div className={classes.cardSub}>Vazio significa ilimitado.</div>
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={`${classes.th} ${classes.thPlain}`}>Franquia</th>
                {plans.map(p => (
                  <th key={p.id} className={`${classes.th} ${classes.thPlain}`} style={{ textAlign: "right" }}>{p.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fields.map(f => (
                <tr key={f.key}>
                  <td className={classes.td}>
                    <div className={classes.strong}>{f.label}</div>
                    {f.hint ? <div className={classes.muted}>{f.hint}</div> : null}
                  </td>
                  {plans.map(p => (
                    <td key={p.id} className={`${classes.td} ${classes.tdNum}`}>
                      {p.limits[f.key] == null
                        ? <span className={classes.muted}>ilimitado</span>
                        : num(p.limits[f.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!edit || creating} onClose={busy ? undefined : () => { setEdit(null); setCreating(false); }} maxWidth="sm" fullWidth PaperProps={{ className: menu.dialogPaper }}>
        <DialogTitle>{creating ? "Novo plano" : `Editar ${edit ? edit.name : ""}`}</DialogTitle>
        <DialogContent dividers>
          {(edit || creating) && (
            <>
              {creating ? (
                <>
                  <div className={classes.cardSub}>
                    Plano dedicado/customizado. O <b>código</b> é fixo depois de criado (usado em código) — use algo estável, ex.: <b>business-clientex</b>.
                  </div>
                  <TextField label="Código (único, sem espaço)" size="small" variant="outlined" margin="dense" fullWidth
                    placeholder="business-clientex" value={form.code || ""}
                    onChange={e => setForm({ ...form, code: e.target.value })} />
                </>
              ) : (
                <div className={classes.cardSub}>
                  {num(edit.usuarios)} cliente(s) neste plano, {num(edit.assinaturasAtivas)} com assinatura ativa. O código <b>{edit.code}</b> não é editável — ele é usado em código.
                </div>
              )}
              <div style={{ display: "flex", gap: 10 }}>
                <TextField label="Nome" size="small" variant="outlined" margin="dense" style={{ flex: 1 }}
                  value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} />
                <TextField label="Mensalidade (R$)" size="small" variant="outlined" margin="dense" style={{ width: 150 }}
                  value={form.preco || ""} onChange={e => setForm({ ...form, preco: e.target.value })} />
                <TextField label="Excedente (×)" size="small" variant="outlined" margin="dense" style={{ width: 120 }}
                  value={form.overageMultiplier || ""} onChange={e => setForm({ ...form, overageMultiplier: e.target.value })} />
              </div>
              <div className={classes.row}>
                <div className={classes.grow}>
                  <div className={classes.strong}>Visível nas telas de planos</div>
                  <div className={classes.muted}>Desligado = plano interno/legado, contratável só pelo backoffice.</div>
                </div>
                <Switch checked={!!form.isPublic} onChange={e => setForm({ ...form, isPublic: e.target.checked })} />
              </div>

              <div className={classes.cardTitle} style={{ marginTop: 12 }}>Franquias</div>
              <div className={classes.cardSub}>Deixe vazio para ilimitado.</div>
              {fields.map(f => (
                <div key={f.key} className={classes.row}>
                  <div className={classes.grow}>
                    <div className={classes.strong}>{f.label}</div>
                    <div className={classes.muted}>{f.unit}{f.hint ? ` · ${f.hint}` : ""}</div>
                  </div>
                  <TextField size="small" variant="outlined" placeholder="ilimitado" style={{ width: 140 }}
                    value={(form.limits && form.limits[f.key]) ?? ""}
                    onChange={e => setForm({ ...form, limits: { ...form.limits, [f.key]: e.target.value } })} />
                </div>
              ))}

              <TextField label="Motivo da alteração (fica na auditoria)" fullWidth size="small" variant="outlined" margin="dense"
                style={{ marginTop: 14 }} value={reason} onChange={e => setReason(e.target.value)} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEdit(null); setCreating(false); }} disabled={busy}>Cancelar</Button>
          <Button
            onClick={creating ? criar : salvar}
            disabled={busy || !reason.trim() || (creating && (!(form.code || "").trim() || !(form.name || "").trim()))}
            color="primary"
            variant="contained"
          >
            {busy ? (creating ? "Criando…" : "Salvando…") : creating ? "Criar plano" : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Shell>
  );
};

export default Plans;
